import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Layers, Search, Sparkles, Zap, X, Repeat2, Anchor } from "lucide-react";
import api, { formatApiError } from "../../api/client";

/**
 * Basket selector + builder for the SwapTicket.
 *
 * Emits a normalized `payload` to the parent whenever the selection is valid:
 *   {
 *     mode: "curated" | "custom",            // just for UX
 *     basket_id?: string,                     // curated / saved custom id
 *     basket?: {name, weighting_mode, notional_mode, constituents:[...]},
 *     notional_mode: "constant" | "dynamic",
 *     basket_id_b?: string,                   // optional second leg for basket-vs-basket
 *   }
 *
 * Parent forwards the payload verbatim to POST /swaps/orders. Backend
 * accepts basket_id/basket_id_b (curated + saved) OR an inline `basket`
 * definition — the ticket sends whichever the user built.
 */

const MAX_CUSTOM = 20;
const MIN_CUSTOM = 2;
const DEFAULT_LIVE = ["BTC", "ETH", "SOL", "BNB", "XRP", "DOGE", "HYPE", "AVAX", "LINK", "TON"];

export default function BasketBuilder({ onChange, singlePairs = [] }) {
    // Curated shelf loaded from /api/baskets/curated
    const [curated, setCurated] = useState([]);
    const [saved, setSaved] = useState([]);
    const [loading, setLoading] = useState(true);

    // UX mode
    const [buildMode, setBuildMode] = useState("curated"); // curated | custom
    const [notionalMode, setNotionalMode] = useState("constant"); // constant | dynamic
    const [enableSecondLeg, setEnableSecondLeg] = useState(false);

    // Curated selection
    const [selectedCuratedId, setSelectedCuratedId] = useState("curated-majors-5");
    const [selectedSecondCuratedId, setSelectedSecondCuratedId] = useState("curated-memes-5");

    // Custom builder state
    const [customName, setCustomName] = useState("");
    const [customConstituents, setCustomConstituents] = useState([]); // [{coin, weight_pct}]
    const [weightingMode, setWeightingMode] = useState("equal"); // equal | custom
    const [coinSearch, setCoinSearch] = useState("");
    const [savingCustom, setSavingCustom] = useState(false);

    // Load curated + saved
    useEffect(() => {
        let alive = true;
        Promise.all([
            api.get("/baskets/curated").catch(() => ({ data: { items: [] } })),
            api.get("/baskets/my").catch(() => ({ data: { items: [] } })),
        ]).then(([c, s]) => {
            if (!alive) return;
            setCurated(c.data?.items || []);
            setSaved(s.data?.items || []);
            setLoading(false);
        });
        return () => { alive = false; };
    }, []);

    // Available coins from live oracle singles.
    const availableCoins = useMemo(() => {
        const bases = (singlePairs || []).map((p) => p.base).filter(Boolean);
        return bases.length ? bases : DEFAULT_LIVE;
    }, [singlePairs]);

    // Filtered coin list for the custom picker
    const filteredCoins = useMemo(() => {
        const q = coinSearch.trim().toUpperCase();
        return availableCoins.filter((c) => (!q || c.includes(q)));
    }, [availableCoins, coinSearch]);

    const totalWeight = useMemo(
        () => customConstituents.reduce((a, c) => a + (parseFloat(c.weight_pct) || 0), 0),
        [customConstituents]
    );

    // Auto-normalize equal weighting whenever count changes.
    useEffect(() => {
        if (weightingMode !== "equal") return;
        setCustomConstituents((prev) => {
            if (prev.length === 0) return prev;
            const w = 1 / prev.length;
            return prev.map((c) => ({ ...c, weight_pct: w }));
        });
    }, [weightingMode, customConstituents.length]); // eslint-disable-line

    // Emit selection upward whenever anything changes.
    useEffect(() => {
        const payload = { notional_mode: notionalMode, mode: buildMode };
        if (buildMode === "curated") {
            payload.basket_id = selectedCuratedId;
            if (enableSecondLeg && selectedSecondCuratedId && selectedSecondCuratedId !== selectedCuratedId) {
                payload.basket_id_b = selectedSecondCuratedId;
            }
        } else {
            // Inline custom definition. Basket must be normalized before send.
            const consts = customConstituents.map((c) => ({
                coin: c.coin,
                weight_pct: weightingMode === "equal" ? (1 / customConstituents.length) : parseFloat(c.weight_pct) || 0,
            }));
            const valid =
                consts.length >= MIN_CUSTOM &&
                consts.length <= MAX_CUSTOM &&
                (weightingMode === "equal" || Math.abs(consts.reduce((a, c) => a + c.weight_pct, 0) - 1) < 0.001);
            payload.basket = valid
                ? {
                      name: customName.trim() || "Custom",
                      weighting_mode: weightingMode,
                      notional_mode: notionalMode,
                      constituents: consts,
                  }
                : null;
        }
        onChange?.(payload);
    }, [
        buildMode, notionalMode, selectedCuratedId, selectedSecondCuratedId, enableSecondLeg,
        customConstituents, weightingMode, customName,
        onChange,
    ]);

    const addCoin = (coin) => {
        if (customConstituents.find((c) => c.coin === coin)) return;
        if (customConstituents.length >= MAX_CUSTOM) {
            toast.error("Basket cap", { description: `Max ${MAX_CUSTOM} constituents.` });
            return;
        }
        setCustomConstituents((prev) => [
            ...prev,
            { coin, weight_pct: weightingMode === "equal" ? 1 / (prev.length + 1) : 0 },
        ]);
    };

    const removeCoin = (coin) => {
        setCustomConstituents((prev) => prev.filter((c) => c.coin !== coin));
    };

    const updateWeight = (coin, val) => {
        const v = parseFloat(val);
        setCustomConstituents((prev) => prev.map((c) => c.coin === coin ? { ...c, weight_pct: Number.isFinite(v) ? v : 0 } : c));
    };

    const normalizeToEqual = () => {
        setWeightingMode("equal");
    };

    const saveCustomBasket = async () => {
        if (!customName.trim()) {
            toast.error("Name required");
            return;
        }
        if (customConstituents.length < MIN_CUSTOM) {
            toast.error("Add at least 2 coins");
            return;
        }
        setSavingCustom(true);
        try {
            const consts = customConstituents.map((c) => ({
                coin: c.coin,
                weight_pct: weightingMode === "equal" ? (1 / customConstituents.length) : parseFloat(c.weight_pct) || 0,
            }));
            const { data } = await api.post("/baskets", {
                name: customName.trim(),
                weighting_mode: weightingMode,
                notional_mode: notionalMode,
                constituents: consts,
            });
            setSaved((prev) => [data, ...prev]);
            toast.success("Basket saved", { description: `${data.name} · ${data.constituents.length} coins` });
        } catch (e) {
            toast.error("Save failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        } finally {
            setSavingCustom(false);
        }
    };

    const selectedCurated = curated.find((b) => b.id === selectedCuratedId);

    return (
        <div className="grid gap-3" data-testid="basket-builder">
            {/* Curated vs Custom */}
            <div className="grid grid-cols-2 gap-1 p-1 bg-[#050507] border border-[#1F1F24] rounded-sm">
                {[
                    { v: "curated", label: "Curated", sub: "presets", icon: Sparkles },
                    { v: "custom", label: "Custom", sub: "your mix", icon: Layers },
                ].map((o) => {
                    const active = buildMode === o.v;
                    const Icon = o.icon;
                    return (
                        <button
                            key={o.v}
                            data-testid={`basket-mode-${o.v}`}
                            onClick={() => setBuildMode(o.v)}
                            className={`py-2 px-3 rounded-sm text-left transition-all ${active
                                ? "bg-gradient-to-br from-cyan-500/15 to-cyan-500/5 border border-cyan-400/60"
                                : "border border-transparent hover:border-zinc-700"}`}
                        >
                            <div className="flex items-center gap-1.5">
                                <Icon className="w-3 h-3 text-cyan-400" />
                                <span className={`text-[11px] font-mono uppercase tracking-widest font-semibold ${active ? "text-cyan-300" : "text-zinc-400"}`}>
                                    {o.label}
                                </span>
                            </div>
                            <div className="text-[9px] font-mono text-zinc-500 mt-0.5">{o.sub}</div>
                        </button>
                    );
                })}
            </div>

            {/* Notional mode */}
            <div>
                <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-zinc-400 mb-1.5">
                    Notional mode
                </div>
                <div className="grid grid-cols-2 gap-1 p-1 bg-[#050507] border border-[#1F1F24] rounded-sm">
                    {[
                        { v: "constant", label: "Constant", sub: "notional fixed for life", icon: Anchor },
                        { v: "dynamic",  label: "Dynamic",  sub: "rebases every 24h",     icon: Repeat2 },
                    ].map((o) => {
                        const active = notionalMode === o.v;
                        const Icon = o.icon;
                        return (
                            <button
                                key={o.v}
                                data-testid={`basket-notionalmode-${o.v}`}
                                onClick={() => setNotionalMode(o.v)}
                                className={`py-1.5 px-3 rounded-sm text-left transition-all ${active
                                    ? "bg-gradient-to-br from-violet-500/15 to-violet-500/5 border border-violet-400/60"
                                    : "border border-transparent hover:border-zinc-700"}`}
                            >
                                <div className="flex items-center gap-1.5">
                                    <Icon className={`w-3 h-3 ${active ? "text-violet-300" : "text-zinc-500"}`} />
                                    <span className={`text-[11px] font-mono uppercase tracking-widest font-semibold ${active ? "text-violet-300" : "text-zinc-400"}`}>
                                        {o.label}
                                    </span>
                                </div>
                                <div className="text-[9px] font-mono text-zinc-500 mt-0.5">{o.sub}</div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Curated selector */}
            {buildMode === "curated" && (
                <div className="grid gap-2">
                    {loading ? (
                        <div className="text-[10px] font-mono text-zinc-500">Loading curated shelf…</div>
                    ) : (
                        <>
                            <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-zinc-400">Pick a basket</div>
                            <div className="grid grid-cols-2 gap-1.5">
                                {curated.map((b) => {
                                    const active = selectedCuratedId === b.id;
                                    const stale = !b.available;
                                    return (
                                        <button
                                            key={b.id}
                                            data-testid={`curated-basket-${b.id}`}
                                            disabled={stale}
                                            onClick={() => setSelectedCuratedId(b.id)}
                                            className={`p-2 rounded-sm text-left border transition-all ${active
                                                ? "border-cyan-400/70 bg-cyan-500/10"
                                                : "border-[#1F1F24] hover:border-zinc-600"} ${stale ? "opacity-40 cursor-not-allowed" : ""}`}
                                        >
                                            <div className={`text-[11px] font-mono font-semibold ${active ? "text-cyan-300" : "text-zinc-300"}`}>{b.name}</div>
                                            <div className="text-[9px] font-mono text-zinc-500 truncate mt-0.5">
                                                {b.constituents.map((c) => c.coin).join(" · ")}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                            {saved.length > 0 && (
                                <>
                                    <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-zinc-400 mt-2">Your saved baskets</div>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {saved.map((b) => {
                                            const active = selectedCuratedId === b.id;
                                            return (
                                                <button
                                                    key={b.id}
                                                    data-testid={`saved-basket-${b.id}`}
                                                    onClick={() => setSelectedCuratedId(b.id)}
                                                    className={`p-2 rounded-sm text-left border transition-all ${active
                                                        ? "border-cyan-400/70 bg-cyan-500/10"
                                                        : "border-[#1F1F24] hover:border-zinc-600"}`}
                                                >
                                                    <div className={`text-[11px] font-mono font-semibold ${active ? "text-cyan-300" : "text-zinc-300"}`}>{b.name}</div>
                                                    <div className="text-[9px] font-mono text-zinc-500 truncate mt-0.5">
                                                        {b.constituents.map((c) => c.coin).join(" · ")} · {b.weighting_mode}
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                            {selectedCurated && (
                                <div className="mt-1 rounded-sm border border-[#1F1F24] bg-[#050507] p-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-400">Composition</span>
                                        <span className="text-[9px] font-mono text-cyan-400">{selectedCurated.weighting_mode}</span>
                                    </div>
                                    <div className="grid gap-0.5">
                                        {selectedCurated.constituents.map((c) => (
                                            <div key={c.coin} className="flex items-center justify-between text-[10px] font-mono">
                                                <span className="text-zinc-300">{c.coin}</span>
                                                <span className="text-zinc-500">{(c.weight_pct * 100).toFixed(1)}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Second leg for basket-vs-basket */}
                            <label className="flex items-center gap-2 text-[10px] font-mono text-zinc-400 mt-1 cursor-pointer" data-testid="basket-vs-basket-toggle">
                                <input
                                    type="checkbox"
                                    checked={enableSecondLeg}
                                    onChange={(e) => setEnableSecondLeg(e.target.checked)}
                                    className="accent-violet-400"
                                />
                                Trade this basket <span className="text-violet-300 font-semibold">against another basket</span> (relative-value)
                            </label>
                            {enableSecondLeg && (
                                <div className="grid grid-cols-2 gap-1.5">
                                    {curated.filter((b) => b.id !== selectedCuratedId).map((b) => {
                                        const active = selectedSecondCuratedId === b.id;
                                        return (
                                            <button
                                                key={b.id}
                                                data-testid={`curated-basket-b-${b.id}`}
                                                onClick={() => setSelectedSecondCuratedId(b.id)}
                                                className={`p-2 rounded-sm text-left border transition-all ${active
                                                    ? "border-violet-400/70 bg-violet-500/10"
                                                    : "border-[#1F1F24] hover:border-zinc-600"}`}
                                            >
                                                <div className={`text-[11px] font-mono font-semibold ${active ? "text-violet-300" : "text-zinc-300"}`}>{b.name}</div>
                                                <div className="text-[9px] font-mono text-zinc-500 truncate mt-0.5">{b.constituents.map((c) => c.coin).join(" · ")}</div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Custom builder */}
            {buildMode === "custom" && (
                <div className="grid gap-2">
                    <div>
                        <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-zinc-400 mb-1">Basket name</div>
                        <input
                            type="text"
                            data-testid="basket-name-input"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder="e.g. AI Layer-1 mix"
                            className="w-full bg-[#050507] border border-[#1F1F24] rounded-sm px-2.5 py-1.5 text-[11px] font-mono text-zinc-100 focus:outline-none focus:border-cyan-400/60"
                        />
                    </div>

                    <div>
                        <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-zinc-400 mb-1">Weighting</div>
                        <div className="grid grid-cols-2 gap-1 p-1 bg-[#050507] border border-[#1F1F24] rounded-sm">
                            {[
                                { v: "equal", label: "Equal-weight", sub: "auto ÷ N" },
                                { v: "custom", label: "Custom %", sub: "must sum to 100%" },
                            ].map((o) => {
                                const active = weightingMode === o.v;
                                return (
                                    <button
                                        key={o.v}
                                        data-testid={`basket-weighting-${o.v}`}
                                        onClick={() => setWeightingMode(o.v)}
                                        className={`py-1.5 px-3 rounded-sm text-left transition-all ${active
                                            ? "bg-gradient-to-br from-cyan-500/15 to-cyan-500/5 border border-cyan-400/60"
                                            : "border border-transparent hover:border-zinc-700"}`}
                                    >
                                        <div className={`text-[11px] font-mono uppercase tracking-widest font-semibold ${active ? "text-cyan-300" : "text-zinc-400"}`}>{o.label}</div>
                                        <div className="text-[9px] font-mono text-zinc-500 mt-0.5">{o.sub}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Selected constituents */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-zinc-400">
                                Constituents ({customConstituents.length}/{MAX_CUSTOM})
                            </div>
                            {weightingMode === "custom" && customConstituents.length > 0 && (
                                <div className={`text-[10px] font-mono tabular-nums ${Math.abs(totalWeight - 1) < 0.001 ? "text-emerald-400" : "text-amber-300"}`}>
                                    Σ {(totalWeight * 100).toFixed(1)}%
                                </div>
                            )}
                        </div>
                        {customConstituents.length === 0 ? (
                            <div className="text-[10px] font-mono text-zinc-500 border border-dashed border-[#1F1F24] rounded-sm p-3 text-center">
                                Add at least 2 coins to build your basket.
                            </div>
                        ) : (
                            <div className="grid gap-1">
                                {customConstituents.map((c) => (
                                    <div key={c.coin} className="flex items-center gap-2 bg-[#050507] border border-[#1F1F24] rounded-sm px-2 py-1.5">
                                        <span className="text-[11px] font-mono text-zinc-200 w-14">{c.coin}</span>
                                        {weightingMode === "equal" ? (
                                            <div className="flex-1 text-[10px] font-mono text-zinc-500 tabular-nums">
                                                {((1 / customConstituents.length) * 100).toFixed(1)}%
                                            </div>
                                        ) : (
                                            <>
                                                <input
                                                    type="range"
                                                    min="0.01"
                                                    max="1"
                                                    step="0.01"
                                                    value={c.weight_pct}
                                                    onChange={(e) => updateWeight(c.coin, e.target.value)}
                                                    className="flex-1 accent-cyan-400 h-1"
                                                    data-testid={`basket-weight-slider-${c.coin}`}
                                                />
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="1"
                                                    step="0.01"
                                                    value={c.weight_pct}
                                                    onChange={(e) => updateWeight(c.coin, e.target.value)}
                                                    className="w-14 bg-[#0A0A0F] border border-[#1F1F24] rounded-sm px-1.5 py-0.5 text-[10px] font-mono text-zinc-100 tabular-nums text-right focus:outline-none focus:border-cyan-400/60"
                                                    data-testid={`basket-weight-input-${c.coin}`}
                                                />
                                            </>
                                        )}
                                        <button
                                            data-testid={`basket-remove-${c.coin}`}
                                            onClick={() => removeCoin(c.coin)}
                                            className="text-zinc-500 hover:text-rose-400"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {weightingMode === "custom" && customConstituents.length > 0 && (
                            <button
                                data-testid="basket-normalize-equal"
                                onClick={normalizeToEqual}
                                className="mt-1 text-[9px] font-mono text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
                            >
                                normalize to equal weights
                            </button>
                        )}
                    </div>

                    {/* Coin picker */}
                    <div>
                        <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-zinc-400 mb-1">Add coins</div>
                        <div className="relative">
                            <Search className="w-3 h-3 text-zinc-500 absolute left-2 top-1/2 -translate-y-1/2" />
                            <input
                                type="text"
                                data-testid="basket-coin-search"
                                value={coinSearch}
                                onChange={(e) => setCoinSearch(e.target.value)}
                                placeholder={`Search ${availableCoins.length} live coins…`}
                                className="w-full bg-[#050507] border border-[#1F1F24] rounded-sm pl-7 pr-2 py-1.5 text-[11px] font-mono text-zinc-100 focus:outline-none focus:border-cyan-400/60"
                            />
                        </div>
                        <div className="max-h-40 overflow-y-auto mt-1.5 grid grid-cols-4 gap-1">
                            {filteredCoins.slice(0, 80).map((coin) => {
                                const selected = customConstituents.find((c) => c.coin === coin);
                                return (
                                    <button
                                        key={coin}
                                        data-testid={`basket-add-${coin}`}
                                        onClick={() => (selected ? removeCoin(coin) : addCoin(coin))}
                                        className={`px-1.5 py-1 text-[10px] font-mono rounded-sm border transition-colors ${selected
                                            ? "border-cyan-400/60 text-cyan-300 bg-cyan-500/10"
                                            : "border-[#1F1F24] text-zinc-400 hover:text-zinc-100 hover:border-zinc-600"}`}
                                    >
                                        {coin}
                                    </button>
                                );
                            })}
                            {filteredCoins.length === 0 && (
                                <div className="col-span-4 text-[10px] font-mono text-zinc-500 py-2 text-center">
                                    No coins match &quot;{coinSearch}&quot;
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Save this basket */}
                    <button
                        data-testid="basket-save-custom"
                        onClick={saveCustomBasket}
                        disabled={savingCustom || customConstituents.length < MIN_CUSTOM || !customName.trim()}
                        className="text-[10px] font-mono uppercase tracking-widest border border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-40 disabled:cursor-not-allowed py-1.5 rounded-sm flex items-center justify-center gap-1.5"
                    >
                        <Zap className="w-3 h-3" />
                        {savingCustom ? "Saving…" : "Save this basket to my library"}
                    </button>
                </div>
            )}
        </div>
    );
}
